import { useState, useCallback } from "react";
import axios from "axios";
import { API } from "../api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Upload, ClipboardPaste } from "lucide-react";

const parseTabularData = (text) => {
  const lines = text.trim().split('\n').filter(line => line.trim());
  if (lines.length === 0) throw new Error("No data provided");
  let startIndex = 0;
  const firstLine = lines[0].split('\t');
  if (firstLine.length >= 5 && (isNaN(parseFloat(firstLine[4])) || firstLine[0].toLowerCase().includes('date'))) {
    startIndex = 1;
  }
  const items = [];
  for (let i = startIndex; i < lines.length; i++) {
    const parts = lines[i].split('\t');
    if (parts.length < 7) continue;
    try {
      items.push({
        invoice_date: parts[0].trim(), sku: parts[1].trim(),
        description: parts[2].trim(), supplier: parts[3].trim(),
        unit_price: parseFloat(parts[4]), quantity: parseFloat(parts[5]),
        total: parseFloat(parts[6])
      });
    } catch { /* skip malformed row */ }
  }
  return items;
};

export const InvoiceUpload = ({ onSuccess }) => {
  const [pasteData, setPasteData] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadPhase, setUploadPhase] = useState("");
  const [uploadDetail, setUploadDetail] = useState("");
  const [uploadResult, setUploadResult] = useState(null);

  const handlePasteSubmit = useCallback(async () => {
    if (!pasteData.trim()) { toast.error("Please paste invoice data"); return; }
    try {
      setLoading(true);
      const items = parseTabularData(pasteData);
      if (items.length === 0) { toast.error("No valid invoice items found"); return; }
      await axios.post(`${API}/invoices`, { items });
      toast.success(`Added ${items.length} invoice items`);
      setPasteData("");
      onSuccess?.();
    } catch (error) {
      toast.error(error.message || "Failed to add invoices");
    } finally { setLoading(false); }
  }, [pasteData, onSuccess]);

  const handleFileUpload = useCallback(async () => {
    if (!uploadFile) { toast.error("Please select a file"); return; }
    try {
      setLoading(true);
      setUploadProgress(0);
      setUploadPhase("uploading");
      setUploadDetail("Uploading file...");
      setUploadResult(null);
      const formData = new FormData();
      formData.append('file', uploadFile);

      const response = await fetch(`${API}/invoices/upload-stream`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        let detail = "Failed to upload file";
        try { detail = JSON.parse(errorText).detail || detail; } catch { /* non-JSON error body */ }
        throw new Error(detail);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let finalResult = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (!line.trim()) continue;
          try {
            const event = JSON.parse(line);
            
            if (event.phase === "reading") {
              setUploadPhase("reading");
              setUploadDetail(event.detail || "Reading file...");
              setUploadProgress(10);
            } else if (event.phase === "parsing") {
              setUploadPhase("parsing");
              setUploadProgress(Math.round(10 + (event.progress || 0) * 0.5));
              setUploadDetail(event.detail || `Parsing rows...`);
            } else if (event.phase === "inserting") {
              setUploadPhase("inserting");
              setUploadProgress(Math.round(60 + (event.progress || 0) * 0.35));
              setUploadDetail(event.detail || "Saving...");
            } else if (event.phase === "complete") {
              setUploadPhase("complete");
              setUploadProgress(100);
              setUploadDetail(event.detail || "Done!");
              finalResult = event;
            } else if (event.phase === "error") {
              throw new Error(event.detail || "Processing failed");
            }
          } catch (parseErr) {
            if (parseErr.message && !parseErr.message.includes("JSON")) throw parseErr;
          }
        }
      }

      if (finalResult) {
        toast.success(finalResult.detail || `Uploaded ${finalResult.count || 0} invoices`);
        setUploadResult(finalResult);
        setUploadFile(null);
        const fileInput = document.querySelector('[data-testid="file-upload-input"]');
        if (fileInput) fileInput.value = "";
        onSuccess?.();
      }
    } catch (error) {
      toast.error(error.message || "Upload failed");
      setUploadPhase("");
      setUploadProgress(0);
      setUploadDetail("");
    } finally { setLoading(false); }
  }, [uploadFile, onSuccess]);

  return (
    <div className="card" style={{ marginBottom: "2rem" }}>
      <Tabs defaultValue="upload" className="w-full">
        <TabsList className="grid w-full grid-cols-2" style={{ marginBottom: "1.5rem" }}>
          <TabsTrigger value="upload" data-testid="upload-tab">
            <Upload size={16} style={{ marginRight: "0.5rem" }} /> Upload File
          </TabsTrigger>
          <TabsTrigger value="paste" data-testid="paste-tab">
            <ClipboardPaste size={16} style={{ marginRight: "0.5rem" }} /> Paste Data
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upload">
          <h2 style={{ fontSize: "1.1rem", fontWeight: "600", marginBottom: "0.75rem", color: "var(--octopus-ice)" }}>
            Upload Invoice File
          </h2>
          <p style={{ color: "var(--octopus-voltage)", marginBottom: "1rem", fontSize: "0.875rem", opacity: 0.8 }}>
            Expected columns: Date, Supplier, Product Code, Description, Quantity, Invoice Reference, Unit Price, Spend Amount
          </p>

          <div className="form-group">
            <input data-testid="file-upload-input" type="file" accept=".csv,.xlsx,.xls"
              onChange={(e) => { setUploadFile(e.target.files[0]); setUploadResult(null); }}
              className="form-input" />
          </div>

          {uploadFile && (
            <div style={{ marginTop: "0.75rem", padding: "0.75rem 1rem", background: "rgba(96, 240, 248, 0.08)", borderRadius: "8px", border: "1px solid rgba(96, 240, 248, 0.2)" }}>
              <p style={{ fontSize: "0.875rem", color: "var(--octopus-ice)" }}>
                <strong>Selected:</strong> {uploadFile.name} ({(uploadFile.size / 1024).toFixed(1)} KB)
              </p>
            </div>
          )}

          {loading && uploadProgress > 0 && (
            <div data-testid="upload-progress-bar" style={{ marginTop: "1rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "var(--octopus-ice)" }}>
                  {uploadPhase === "uploading" ? "Uploading..."
                    : uploadPhase === "reading" ? "Reading file..."
                    : uploadPhase === "parsing" ? "Parsing rows..."
                    : uploadPhase === "inserting" ? "Saving to database..."
                    : uploadPhase === "complete" ? "Complete!"
                    : "Processing..."}
                </span>
                <span style={{ fontSize: "0.875rem", color: "var(--octopus-voltage)", fontVariantNumeric: "tabular-nums" }}>{uploadProgress}%</span>
              </div>
              {uploadDetail && (
                <p data-testid="upload-progress-detail" style={{ fontSize: "0.8rem", color: "var(--octopus-voltage)", opacity: 0.7, marginBottom: "0.5rem", fontVariantNumeric: "tabular-nums" }}>
                  {uploadDetail}
                </p>
              )}
              <div style={{ width: "100%", height: "6px", background: "rgba(96, 240, 248, 0.15)", borderRadius: "4px", overflow: "hidden" }}>
                <div style={{
                  width: "100%", height: "100%",
                  background: uploadPhase === "complete"
                    ? "var(--octopus-green)"
                    : "linear-gradient(90deg, var(--octopus-pink), var(--octopus-voltage))",
                  transform: `scaleX(${uploadProgress / 100})`,
                  transformOrigin: "left",
                  transition: "transform 0.3s ease, background 0.3s ease"
                }} />
              </div>
            </div>
          )}

          {uploadResult && (
            <div style={{
              marginTop: "1rem", padding: "1rem",
              background: uploadResult.count > 0 ? "rgba(24, 240, 184, 0.1)" : "rgba(240, 80, 248, 0.1)",
              borderRadius: "8px", border: `1px solid ${uploadResult.count > 0 ? "var(--octopus-green)" : "var(--octopus-pink)"}`
            }}>
              <p style={{ fontSize: "0.9rem", fontWeight: "600", color: "var(--octopus-ice)", marginBottom: "0.5rem" }}>
                {uploadResult.message}
              </p>
              {uploadResult.unmapped_suppliers?.length > 0 && (
                <p style={{ fontSize: "0.85rem", color: "var(--octopus-voltage)", opacity: 0.8 }}>
                  Unmapped suppliers: {uploadResult.unmapped_suppliers.join(", ")} &mdash; add mappings in Settings to include these
                </p>
              )}
            </div>
          )}

          <div style={{ marginTop: "1.5rem", display: "flex", gap: "1rem" }}>
            <Button data-testid="upload-file-btn" onClick={handleFileUpload} disabled={loading || !uploadFile} className="btn btn-primary">
              {loading ? "Processing..." : "Upload File"}
            </Button>
            <Button data-testid="clear-file-btn" onClick={() => { setUploadFile(null); setUploadResult(null); }} variant="outline" disabled={!uploadFile}>
              Clear
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="paste">
          <h2 style={{ fontSize: "1.1rem", fontWeight: "600", marginBottom: "0.75rem", color: "var(--octopus-ice)" }}>
            Paste Invoice Data
          </h2>
          <p style={{ color: "var(--octopus-voltage)", marginBottom: "1rem", fontSize: "0.875rem", opacity: 0.8 }}>
            Format: Date [tab] SKU [tab] Description [tab] Supplier [tab] Unit Price [tab] Quantity [tab] Total
          </p>
          <textarea data-testid="invoice-paste-area" className="paste-area"
            placeholder="Paste invoice data here (tab-separated)..."
            value={pasteData} onChange={(e) => setPasteData(e.target.value)} />
          <div style={{ marginTop: "1.5rem", display: "flex", gap: "1rem" }}>
            <Button data-testid="submit-invoices-btn" onClick={handlePasteSubmit} disabled={loading || !pasteData.trim()} className="btn btn-primary">
              {loading ? "Processing..." : "Add Invoices"}
            </Button>
            <Button data-testid="clear-paste-btn" onClick={() => setPasteData("")} variant="outline" disabled={!pasteData.trim()}>
              Clear
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default InvoiceUpload;
